import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { request } from '@/lib/http';
import { pagina, porCursor } from '@/lib/paginacao';
import {
  type AtualizarPerfilRequest,
  type AtualizarUsuarioRequest,
  type CriarPerfilRequest,
  type CriarUsuarioRequest,
  type DefinirSenhaRequest,
  perfilSchema,
  usuarioResumoSchema,
} from '@fiscoai/contracts';

export function useUsuarios(q: string) {
  return useInfiniteQuery({
    queryKey: ['usuarios', q],
    queryFn: ({ pageParam, signal }) =>
      request('/usuarios', {
        schema: pagina(usuarioResumoSchema),
        query: { q: q || undefined, cursor: pageParam },
        signal,
      }),
    ...porCursor,
  });
}

export function useAlterarUsuarios() {
  const cliente = useQueryClient();
  const recarregar = () => cliente.invalidateQueries({ queryKey: ['usuarios'] });
  return {
    criar: useMutation({
      mutationFn: (dados: CriarUsuarioRequest) =>
        request('/usuarios', { method: 'POST', body: dados, schema: usuarioResumoSchema }),
      onSuccess: recarregar,
    }),
    atualizar: useMutation({
      mutationFn: ({ id, dados }: { id: string; dados: AtualizarUsuarioRequest }) =>
        request(`/usuarios/${id}`, { method: 'PATCH', body: dados, schema: usuarioResumoSchema }),
      onSuccess: recarregar,
    }),
    /** 204: o server marca a troca obrigatória e encerra as sessões da pessoa. */
    definirSenha: useMutation({
      mutationFn: ({ id, senha }: { id: string; senha: string }) =>
        request(`/usuarios/${id}/senha`, {
          method: 'PUT',
          body: { senha } satisfies DefinirSenhaRequest,
          schema: z.undefined(),
        }),
    }),
  };
}

export function usePerfis() {
  return useQuery({
    queryKey: ['perfis'],
    queryFn: ({ signal }) => request('/perfis', { schema: z.array(perfilSchema), signal }),
  });
}

export function useAlterarPerfis() {
  const cliente = useQueryClient();
  const recarregar = () => cliente.invalidateQueries({ queryKey: ['perfis'] });
  return {
    criar: useMutation({
      mutationFn: (dados: CriarPerfilRequest) =>
        request('/perfis', { method: 'POST', body: dados, schema: perfilSchema }),
      onSuccess: recarregar,
    }),
    atualizar: useMutation({
      mutationFn: ({ id, dados }: { id: string; dados: AtualizarPerfilRequest }) =>
        request(`/perfis/${id}`, { method: 'PATCH', body: dados, schema: perfilSchema }),
      onSuccess: recarregar,
    }),
  };
}
